// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: assistant dashboards on the client. One entry per dashboard (or shared copy), so
// the card in the chat, its full-screen view and the Home tile show the same period, the
// same version and the same numbers, and a refresh in one place shows everywhere.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {RETRY_RATE_LIMITS} from '@app/features/platform/transport/RestRetry';
import {http} from '@app/features/platform/transport/RestTransport';
import {HttpError} from '@app/features/platform/types/EndpointError';
import type {
	DashboardDrillRequest,
	DashboardDrillResponse,
	DashboardPeriod,
	DashboardSummary,
	DashboardView,
} from '@konvo/schema/src/domains/konvo/AssistantDashboard';
import {makeAutoObservable, runInAction} from 'mobx';

/** A live dashboard of the user's, or a frozen copy shared into a chat. */
export type DashboardTarget = {kind: 'dashboard'; id: string} | {kind: 'share'; id: string};

export interface DashboardEntry {
	view: DashboardView | null;
	loading: boolean;
	error: string | null;
	/** What the user picked on the card; null follows the dashboard's own period and version. */
	period: DashboardPeriod | null;
	version: number | null;
	/** Chart or table, per panel id; '*' flips every panel. */
	tables: Record<string, boolean>;
	/** Last time a load was asked for, to drop stale responses. */
	request: number;
}

export function targetKey(target: DashboardTarget): string {
	return `${target.kind}:${target.id}`;
}

function errorText(error: unknown, fallback: string): string {
	if (error instanceof HttpError) {
		const body = error.body as {error?: unknown} | undefined;
		if (body && typeof body.error === 'string') return body.error;
	}
	return fallback;
}

function periodQuery(period: DashboardPeriod | null): Record<string, string> {
	if (!period) return {};
	return 'preset' in period ? {period: period.preset} : {start: period.start, end: period.end};
}

class DashboardsStore {
	entries = new Map<string, DashboardEntry>();
	summaries: Array<DashboardSummary> | null = null;
	summariesLoading = false;
	summariesError: string | null = null;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	entry(target: DashboardTarget): DashboardEntry {
		const key = targetKey(target);
		let entry = this.entries.get(key);
		if (!entry) {
			entry = {view: null, loading: false, error: null, period: null, version: null, tables: {}, request: 0};
			this.entries.set(key, entry);
		}
		return entry;
	}

	/** The entry if one exists; safe to call while rendering (never creates state). */
	peek(target: DashboardTarget): DashboardEntry | undefined {
		return this.entries.get(targetKey(target));
	}

	/**
	 * Loads the dashboard unless it is already here. A card redrawn with a newer version
	 * (the assistant changed the dashboard) reloads to show it.
	 */
	ensure(target: DashboardTarget, version?: number): void {
		const entry = this.entry(target);
		if (entry.loading) return;
		const stale = version !== undefined && entry.view !== null && entry.view.latest_version < version;
		if (entry.view && !stale && !entry.error) return;
		if (stale) entry.version = null;
		void this.load(target);
	}

	async load(target: DashboardTarget, options: {refresh?: boolean} = {}): Promise<void> {
		const entry = this.entry(target);
		const request = entry.request + 1;
		entry.request = request;
		entry.loading = true;
		entry.error = null;
		try {
			const response =
				target.kind === 'share'
					? await http.get<DashboardView>(Endpoints.KONVO_ASSISTANT_DASHBOARD_SHARED(target.id), RETRY_RATE_LIMITS)
					: await http.get<DashboardView>(Endpoints.KONVO_ASSISTANT_DASHBOARD(target.id), {
							query: {
								...periodQuery(entry.period),
								...(entry.version ? {version: String(entry.version)} : {}),
								...(options.refresh ? {refresh: '1'} : {}),
							},
							timeoutMs: 90_000,
							// A 429 (many cards loading at once after a reload) waits instead of
							// showing "could not be loaded"; a slow server is not asked twice more.
							...RETRY_RATE_LIMITS,
							retries: 1,
						});
			runInAction(() => {
				if (entry.request !== request) return;
				entry.view = response.body;
				entry.loading = false;
			});
		} catch (error) {
			runInAction(() => {
				if (entry.request !== request) return;
				entry.loading = false;
				entry.error = errorText(error, 'This dashboard could not be loaded.');
			});
		}
	}

	setPeriod(target: DashboardTarget, period: DashboardPeriod | null): void {
		const entry = this.entry(target);
		entry.period = period;
		void this.load(target);
	}

	setVersion(target: DashboardTarget, version: number | null): void {
		const entry = this.entry(target);
		entry.version = version;
		void this.load(target);
	}

	refresh(target: DashboardTarget): Promise<void> {
		return this.load(target, {refresh: true});
	}

	isTable(target: DashboardTarget, panelId: string): boolean {
		const tables = this.peek(target)?.tables ?? {};
		return tables[panelId] ?? tables['*'] ?? false;
	}

	toggleTable(target: DashboardTarget, panelId: string): void {
		const entry = this.entry(target);
		entry.tables = {...entry.tables, [panelId]: !this.isTable(target, panelId)};
	}

	toggleAllTables(target: DashboardTarget): void {
		const entry = this.entry(target);
		entry.tables = {'*': !(entry.tables['*'] ?? false)};
	}

	async drill(dashboardId: string, request: DashboardDrillRequest): Promise<DashboardDrillResponse> {
		const response = await http.post<DashboardDrillResponse>(Endpoints.KONVO_ASSISTANT_DASHBOARD_DRILL(dashboardId), {
			body: request,
			timeoutMs: 60_000,
		});
		return response.body;
	}

	/** Save, pin, rename; updates every open view of it. */
	async update(dashboardId: string, change: {saved?: boolean; pinned?: boolean; title?: string}): Promise<void> {
		const response = await http.patch<DashboardSummary>(Endpoints.KONVO_ASSISTANT_DASHBOARD(dashboardId), {
			body: change,
		});
		runInAction(() => {
			const entry = this.entries.get(targetKey({kind: 'dashboard', id: dashboardId}));
			if (entry?.view) {
				entry.view = {
					...entry.view,
					saved: response.body.saved,
					pinned: response.body.pinned,
					title: response.body.title,
				};
			}
			if (this.summaries) {
				const others = this.summaries.filter((summary) => summary.id !== dashboardId);
				this.summaries = [...others, response.body].sort(compareSummaries);
			}
		});
	}

	async remove(dashboardId: string): Promise<void> {
		await http.post(Endpoints.KONVO_ASSISTANT_DASHBOARD_DELETE(dashboardId), {body: {}});
		runInAction(() => {
			this.entries.delete(targetKey({kind: 'dashboard', id: dashboardId}));
			this.summaries = this.summaries?.filter((summary) => summary.id !== dashboardId) ?? null;
		});
	}

	async share(
		dashboardId: string,
		body: {channel_id: string; content?: string; period?: DashboardPeriod; version?: number},
	): Promise<void> {
		await http.post(Endpoints.KONVO_ASSISTANT_DASHBOARD_SHARE(dashboardId), {body, timeoutMs: 90_000});
	}

	/** Posts the dashboard's card into the Assistant chat again. */
	async show(dashboardId: string): Promise<void> {
		await http.post(Endpoints.KONVO_ASSISTANT_DASHBOARD_SHOW(dashboardId), {body: {}});
	}

	async loadSummaries(): Promise<void> {
		if (this.summariesLoading) return;
		this.summariesLoading = true;
		this.summariesError = null;
		try {
			const response = await http.get<{dashboards: Array<DashboardSummary>}>(Endpoints.KONVO_ASSISTANT_DASHBOARDS);
			runInAction(() => {
				this.summaries = [...response.body.dashboards].sort(compareSummaries);
				this.summariesLoading = false;
			});
		} catch (error) {
			runInAction(() => {
				this.summariesLoading = false;
				this.summariesError = errorText(error, 'Dashboards could not be loaded.');
			});
		}
	}

	get pinned(): Array<DashboardSummary> {
		return (this.summaries ?? []).filter((summary) => summary.pinned);
	}

	async movePin(dashboardId: string, offset: -1 | 1): Promise<void> {
		const order = this.pinned.map((summary) => summary.id);
		const index = order.indexOf(dashboardId);
		const next = index + offset;
		if (index < 0 || next < 0 || next >= order.length) return;
		[order[index], order[next]] = [order[next]!, order[index]!];
		runInAction(() => {
			if (!this.summaries) return;
			this.summaries = this.summaries
				.map((summary) => (summary.pinned ? {...summary, pin_order: order.indexOf(summary.id) + 1} : summary))
				.sort(compareSummaries);
		});
		await http.post(Endpoints.KONVO_ASSISTANT_DASHBOARD_PIN_ORDER, {body: {ids: order}});
	}
}

/** Pinned first in pin order, then saved, then the rest, newest first. */
function compareSummaries(a: DashboardSummary, b: DashboardSummary): number {
	if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
	if (a.pinned) return a.pin_order - b.pin_order;
	if (a.saved !== b.saved) return a.saved ? -1 : 1;
	return Date.parse(b.updated_at) - Date.parse(a.updated_at);
}

export default new DashboardsStore();
